import React from 'react';
import { Star, ArrowRight, Sparkles, PlayCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BrowserFrame from './BrowserFrame';
import { MockWeddingDashboard } from './ProductMockups';

export default function Hero() {
  const navigate = useNavigate();
  return (
    <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden hero-gradient">
      <div className="absolute inset-0 bg-noise opacity-40 pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#F4D6CE] blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#EFD9C9] blur-3xl opacity-50 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 animate-float-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full tag-chip text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Plan your wedding, calmly.
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#2A2A2A]">
              Plan the wedding<br />
              you've actually<br />
              been <span className="italic text-[#7B2C3B]">picturing</span>.
            </h1>
            <p className="mt-6 text-lg text-[#4A4A4A] max-w-xl leading-relaxed">
              One quiet workspace for the guest list, invitations, RSVPs, budget, seating and every fitting in between &mdash; with beautifully-designed email &amp; SMS invitations and a thoughtful AI friend who's been around more weddings than your favorite aunt.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Start planning today
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                className="inline-flex items-center gap-2 text-sm font-medium text-[#2A2A2A] hover:text-[#7B2C3B] transition-colors"
                onClick={() => navigate('/dashboard')}
              >
                <PlayCircle className="w-5 h-5" />
                See it in action
              </button>
            </div>

            <div className="mt-10 flex items-center gap-6">
              <div className="flex -space-x-3">
                {[
                  'https://images.unsplash.com/photo-1502635385003-ee1e6a1a742d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHx3ZWRkaW5nJTIwZWxlZ2FudHxlbnwwfHx8fDE3Nzk5MTIwNjZ8MA&ixlib=rb-4.1.0&q=85',
                  'https://images.unsplash.com/photo-1595467959554-9ffcbf37f10f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxicmlkYWwlMjBmbG93ZXJzfGVufDB8fHx8MTc3OTkxMjA2Nnww&ixlib=rb-4.1.0&q=85',
                  'https://images.pexels.com/photos/30190595/pexels-photo-30190595.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
                  'https://images.unsplash.com/photo-1561848355-890d054dc55a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxicmlkYWwlMjBmbG93ZXJzfGVufDB8fHx8MTc3OTkxMjA2Nnww&ixlib=rb-4.1.0&q=85'
                ].map((src, i) => (
                  <div key={i} className="w-9 h-9 rounded-full border-2 border-[#FBF7F2] overflow-hidden bg-[#EFE3D8]">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-[#B8896C]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#B8896C]" />
                  ))}
                </div>
                <p className="text-xs text-[#4A4A4A] mt-0.5">Verified five-star reviews</p>
              </div>
            </div>
          </div>

          {/* Hero product screenshot — the Wedding Dashboard, framed */}
          <div className="lg:col-span-6 relative animate-float-up" style={{ animationDelay: '120ms' }}>
            <BrowserFrame url="yourweddingfriends.com/dashboard" className="max-w-xl mx-auto">
              <MockWeddingDashboard />
            </BrowserFrame>

            <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-4 w-56 shadow-xl animate-float-up" style={{ animationDelay: '320ms' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F4D6CE] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#7B2C3B]" />
                </div>
                <div>
                  <p className="text-xs text-[#6A6A6A]">AI Friend</p>
                  <p className="font-serif text-sm text-[#2A2A2A] leading-tight">On call 24/7</p>
                </div>
              </div>
            </div>

            <div className="absolute -top-4 -right-4 glass-card rounded-2xl px-4 py-3 shadow-xl animate-float-up whitespace-nowrap" style={{ animationDelay: '500ms' }}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse-soft" />
                <p className="text-xs font-medium text-[#2A2A2A]">112 of 148 RSVPs in</p>
              </div>
            </div>

            <div className="absolute top-1/2 -right-6 -translate-y-1/2 hidden lg:block">
              <span className="font-script text-2xl text-[#7B2C3B] -rotate-12 inline-block">
                breathe, love.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee — value props, not fake media mentions */}
      <div className="mt-20 border-y border-[#EFE3D8] bg-[#FBF7F2]/40 py-4 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-10 px-6">
              {[
                'Free to start',
                'Ends automatically after your wedding',
                'Email + SMS invitations',
                'AI Wedding Friend included',
                'Never more than $149 on Beloved',
                'Pause anytime',
                'Built for modern couples',
                'Works on every device',
              ].map((t, i) => (
                <span key={`${k}-${i}`} className="font-serif text-lg text-[#8a6f60] tracking-wide inline-flex items-center gap-3">
                  {t}
                  <span className="w-1 h-1 rounded-full bg-[#B8896C] opacity-60" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
